import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  Platform,
  StatusBar,
} from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ResidentMobileHeaderWithMenu } from '@/components/residents/mobile/ResidentMobileHeaderWithMenu';
import { useResidentScreen } from '@/hooks/residents/useResidentScreen';
import { useAppDispatch, useAppSelector } from '@/store';
import { refreshUser } from '@/store/slices/authSlice';
import { fetchLinkedAccounts } from '@/store/slices/residents/homeSlice';
import { profileStrings as strings, commonStrings } from '@/constants/residents/strings';
import { LinkedAccountsCard } from '@/components/residents/home/LinkedAccountsCard';
import { AuthAPI } from '@/services/residents/api/AuthAPI';
import { ScreenLoadingOverlay } from '@/components/common/ScreenLoadingOverlay';
import { ChevronListItem } from '@/components/common/residents/ChevronListItem';
import { formatDisplayName } from '@/utils/residents/user';
import { buildFeedbackUrl } from '@/utils/residents/links';
import { ProfileHeader } from '@/screens/residents/profile/components/ProfileHeader';
import { buildProfileMenu, accessibilityKeyMap } from '@/screens/residents/profile/menu';
import { showAlert } from '@/utils/common/alert';

type MenuItem =
  | { key: 'connections'; type: 'connections' }
  | { key: 'profile' | 'password' | 'paymentMethods' | 'autopay' | 'wifi' | 'feedback' | 'roommates' | 'howItWorks' | 'tutorial' | 'deleteAccount'; type: 'action'; title: string; onPress: () => void };

export const ResidentProfileScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  const insets = useSafeAreaInsets();
  const { user } = useAppSelector((state) => state.auth);
  const { linkedAccounts } = useAppSelector((state) => state.home);
  
  const shouldShowPayments = user?.customerType !== 'non-leasing occupant';

  // Use resident screen hook for navigation handling
  const {
    handleNavigate,
    handleLogoPress,
    handleSignOut,
    handleNotificationPress,
  } = useResidentScreen('ResidentProfile', navigation);

  // Load fresh user and connections when screen gains focus
  useFocusEffect(
    useCallback(() => {
      dispatch(refreshUser());
      dispatch(fetchLinkedAccounts());
    }, [dispatch])
  );

  const displayName = useMemo(() => formatDisplayName(user as any), [user]);

  const unitLabel = useMemo(() => {
    if (!user?.unitNumberSpace) return undefined;
    try {
      return strings.user.unit(user.unitNumberSpace);
    } catch {
      return `Unit #${user.unitNumberSpace}`;
    }
  }, [user]);

  const [appVersion, setAppVersion] = useState<string>('');
  useEffect(() => {
    try {
      const pkg = require('../../../../package.json');
      setAppVersion(pkg.version ? `v${pkg.version}` : '');
    } catch {
      setAppVersion('');
    }
  }, []);

  const [isSendingVerification, setIsSendingVerification] = useState<boolean>(false);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const onChangePassword = useCallback(async () => {
    const email = user?.email;
    if (!email) {
      showAlert('Error', 'No email associated with this account.');
      return;
    }
    try {
      if (isMounted.current) setIsSendingVerification(true);
      const result = await AuthAPI.sendVerification(email);
      if (result?.success) {
        navigation.navigate('AccountVerification', { email, isSignup: false });
      } else {
        showAlert('Error', result?.message || 'Failed to send verification email.');
      }
    } catch (e: any) {
      const message = e?.message || 'Failed to send verification email.';
      showAlert('Error', message);
    } finally {
      if (isMounted.current) setIsSendingVerification(false);
    }
  }, [navigation, user?.email]);

  const isWifiFeatureEnabled = useMemo(() => {
    try {
      const flags = (user as any)?.property?.featureFlags?.flags || {};
      if (typeof flags.wifiEnabled === 'boolean') return !!flags.wifiEnabled;
      if (typeof flags['cbx-wifi-integration-enabled'] === 'boolean') return !!flags['cbx-wifi-integration-enabled'];
      return false;
    } catch {
      return false;
    }
  }, [user]);

  function onOpenFeedback() {
    const url = buildFeedbackUrl(user?.email);
    navigation.navigate('WebView', { url, title: strings.option.feedback });
  }

  const menuItems: MenuItem[] = useMemo(() => {
    const baseItems = buildProfileMenu(isWifiFeatureEnabled, shouldShowPayments);
    const withHandlers: MenuItem[] = baseItems.map((i) => {
      if (i.type === 'connections') return i;
      switch (i.key) {
        case 'profile':
          return { ...i, onPress: () => navigation.navigate('ProfileBio') } as MenuItem;
        case 'password':
          return { ...i, onPress: onChangePassword } as MenuItem;
        case 'paymentMethods':
          return { ...i, onPress: () => navigation.navigate('MobileAccountSettings', { section: 'paymentMethods' }) } as MenuItem;
        case 'autopay':
          return { ...i, onPress: () => navigation.navigate('MobileAccountSettings', { section: 'autopay' }) } as MenuItem;
        case 'wifi':
          return { ...i, onPress: () => navigation.navigate('WifiConnection') } as MenuItem;
        case 'roommates':
          return { ...i, onPress: () => navigation.navigate('Roommates') } as MenuItem;
        case 'howItWorks':
          return { ...i, onPress: () => navigation.navigate('HowItWorks') } as MenuItem;
        case 'tutorial':
          return { ...i, onPress: () => navigation.navigate('Tutorial') } as MenuItem;
        case 'feedback':
          return { ...i, onPress: onOpenFeedback } as MenuItem;
        case 'deleteAccount':
          return { ...i, onPress: () => navigation.navigate('DeleteAccount') } as MenuItem;
        default:
          return i as MenuItem;
      }
    }) as MenuItem[];
    return withHandlers;
  }, [navigation, isWifiFeatureEnabled, shouldShowPayments, onChangePassword]);

  const renderMenuItem = useCallback(({ item }: { item: MenuItem }) => {
    if (item.type === 'connections') {
      return (
        <View style={styles.cardWrapper}>
          <LinkedAccountsCard
            linkedAccounts={linkedAccounts}
            onPress={() => handleNavigate('/linked-accounts')}
          />
        </View>
      );
    }
    const accessibilityId = accessibilityKeyMap[item.key] || item.key;
    const isDisabled = item.key === 'password' && isSendingVerification;
    return (
      <ChevronListItem
        title={item.title}
        onPress={item.onPress}
        disabled={isDisabled}
        accessibilityId={accessibilityId}
        cellStyle={styles.cell}
        cellDisabledStyle={styles.cellDisabled}
        titleStyle={styles.cellTitle}
      />
    );
  }, [linkedAccounts, handleNavigate, isSendingVerification]);

  const listHeader = (
    <ProfileHeader
      title={strings.title}
      displayName={displayName}
      unitLabel={unitLabel}
      photoUrl={(user?.photoUrl || user?.profilePictureUrl) as string | undefined}
      onPressSettings={() => navigation.navigate('MobileAccountSettings')}
      styles={{
        headerView: styles.headerView,
        headerTopRow: styles.headerTopRow,
        headerTitle: styles.headerTitle,
        settingsButton: styles.settingsButton,
        profileRow: styles.profileRow,
        profileTextCol: styles.profileTextCol,
        nameLabel: styles.nameLabel,
        addressLabel: styles.addressLabel,
        avatarContainer: styles.avatarContainer,
      }}
    />
  );

  const listFooter = (
    <View style={styles.footer}>
      <Text style={styles.versionLabel}>{appVersion}</Text>
    </View>
  );

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" />
      
      {/* New Figma Header with Navigation Menu */}
      <ResidentMobileHeaderWithMenu
        onLogoPress={handleLogoPress}
        onSignOut={handleSignOut}
        onNavigate={handleNavigate}
        onNotificationPress={handleNotificationPress}
        currentPath="/my-account"
        currentTitle="My account"
        showNotification={true}
      />

      <FlatList
        data={menuItems}
        keyExtractor={(i) => i.key}
        renderItem={renderMenuItem}
        ListHeaderComponent={listHeader}
        ListFooterComponent={listFooter}
        contentContainerStyle={[styles.listContent, { paddingBottom: 24 + insets.bottom }]}
      />
      <ScreenLoadingOverlay visible={isSendingVerification} message={commonStrings.sending} variant="resident" />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#171717',
  },
  listContent: {
    paddingBottom: 24,
    paddingHorizontal: 16,
  },
  headerView: {
    backgroundColor: '#171717',
    paddingBottom: 8,
  },
  headerTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 16,
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: Platform.select({ ios: '800', android: '700', default: '700' }),
    color: '#EEF1F4',
  },
  settingsButton: {
    height: 36,
    width: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  profileRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 16,
    paddingBottom: 12,
  },
  profileTextCol: {
    flex: 1,
    paddingRight: 12,
  },
  nameLabel: {
    fontSize: 18,
    fontWeight: Platform.select({ ios: '700', android: '600', default: '700' }),
    color: '#EEF1F4',
  },
  addressLabel: {
    fontSize: 14,
    color: '#919191',
    marginTop: 6,
  },
  avatarContainer: {
    height: 64,
    width: 64,
    borderRadius: 32,
    borderWidth: 1,
    borderColor: '#2F2F2F',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#464646',
  },
  cell: {
    backgroundColor: '#171717',
    paddingVertical: 14,
    borderBottomColor: '#2F2F2F',
    borderBottomWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cellTitle: {
    fontSize: 15,
    color: '#EEF1F4',
  },
  cellDisabled: {
    opacity: 0.5,
  },
  cardWrapper: {
    marginTop: 8,
    marginBottom: 8,
  },
  footer: {
    paddingVertical: 24,
    alignItems: 'center',
    gap: 16,
    backgroundColor: '#171717',
  },
  versionLabel: {
    color: '#919191',
    fontSize: 12,
  },
});

